import { useState } from "react";
import { toast } from "sonner";
import { ContactDialog } from "@/components/ContactDialog";
import { CONTACT_ROLES, contactLine, hasContact } from "@/lib/contacts";
import { Copy, Pencil, Plus, ChevronDown } from "lucide-react";
import { MenuOrSheet } from "@/components/ActionSheet";
import { PersonLine } from "@/components/PersonLine";

const card = "bg-white rounded-[16px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";
const hasData = (c) => !!(c && (c.name || c.entity || c.phone || c.email));

// Outro contacto no mesmo formato das pessoas: nome, papel · telefone, email e ícones; sem dados, «+ Adicionar».
function ContactRow({ r, c, onEdit }) {
  const copy = async () => { await navigator.clipboard.writeText(contactLine(c)).catch(() => {}); toast.success("Contacto copiado"); };
  const title = c.name || c.entity;
  return (
    <PersonLine testid={`contact-row-${r.key}`} phone={c.phone} email={c.email} onAddPhone={onEdit || undefined} onAddEmail={onEdit || undefined}
      title={<span data-testid={`contact-value-${r.key}`}>{title}</span>}
      role={<>{[r.label, c.name && c.entity].filter(Boolean).join(" · ")}{c.auto_short && <span className="text-slate-400" data-testid={`contact-auto-${r.key}`}> · lido {c.auto_short}</span>}</>}
      more={[{ label: "Copiar contacto", icon: Copy, onClick: copy, testid: `contact-copy-${r.key}` },
        onEdit && { label: "Editar", icon: Pencil, onClick: onEdit, testid: `contact-edit-${r.key}` }].filter(Boolean)} />
  );
}

function AddContact({ roles, onPick }) {
  if (!roles.length) return null;
  return (
    <MenuOrSheet title="Adicionar contacto" align="start" testid="contact-add-menu"
      trigger={<button className="w-full flex items-center gap-1.5 py-2.5 border-t border-slate-100 first:border-t-0 text-sm font-medium text-ec-green hover:underline" data-testid="contact-add">
          <Plus size={15} /> Adicionar contacto
        </button>}
      items={roles.map((r) => ({ label: r.label, onClick: () => onPick(r.key), testid: `contact-add-role-${r.key}` }))} />
  );
}

export function ContactsCard({ p, onDone, embedded = false, ro = false }) {
  const [role, setRole] = useState(null);
  const [open, setOpen] = useState(false);
  const contacts = p.contacts || {};
  const relevant = CONTACT_ROLES.filter((r) => contacts[r.key]?.relevant);
  const shown = relevant.filter((r) => hasData(contacts[r.key]));
  const addable = [...relevant, ...CONTACT_ROLES.filter((r) => !contacts[r.key]?.relevant)].filter((r) => !shown.includes(r));
  const done = relevant.filter((r) => hasContact(contacts[r.key])).length;
  const lacking = relevant.filter((r) => !hasContact(contacts[r.key]));
  const state = lacking.length ? `falta indicar ${lacking.map((r) => r.label.toLowerCase()).join(", ")}` : relevant.length ? "todos indicados" : "nenhum necessário";
  const body = (
    <>
      {shown.map((r) => <ContactRow key={r.key} r={r} c={contacts[r.key]} onEdit={ro ? null : () => setRole(r.key)} />)}
      {!ro && embedded && p.stage !== "angariacao" && lacking.filter((r) => !shown.includes(r)).map((r) => (
        <button key={r.key} onClick={() => setRole(r.key)} className="block w-full text-left py-2 border-t border-slate-100 first:border-t-0 text-sm font-medium text-ec-green hover:underline" data-testid={`contact-add-missing-${r.key}`}>
          + Adicionar {r.label.toLowerCase()}
        </button>
      ))}
      {!ro && <AddContact roles={addable} onPick={setRole} />}
      <ContactDialog pid={p.id} role={role} initial={role && contacts[role]} onClose={() => setRole(null)}
        onSaved={() => { setRole(null); onDone(); }} />
    </>
  );
  if (embedded) {
    if (ro && !shown.length) return null;
    return (
      <div className="pt-3 mt-1 border-t border-slate-200" data-testid="contacts-card">
        <div className="text-[11px] font-semibold tracking-[0.12em] text-slate-400 uppercase">Outros contactos</div>
        <div data-testid="contacts-body">{body}</div>
      </div>
    );
  }
  return (
    <div className={card} data-testid="contacts-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center px-6 py-4 text-left" data-testid="contacts-toggle">
        <span className="mr-auto" data-testid="contacts-summary"><span className="text-xs font-semibold tracking-[0.12em] text-slate-500 uppercase">Outros contactos</span>
          <span className={`block text-sm mt-0.5 ${lacking.length ? "text-amber-700" : "text-slate-500"}`} data-testid="contacts-state" data-done={done}>{state[0].toUpperCase() + state.slice(1)}</span></span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-3 -mt-1" data-testid="contacts-body">{body}</div>}
    </div>
  );
}
